import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CountUp from "../ui/CountUp";
import BookAuditButton from "../ui/BookAuditButton";
import { useCurrency } from "../../../../hooks/useCurrency";

const rotatingWords = ["Optimized", "Automated", "Streamlined", "Efficient", "Transformed", "AI first"];

/* ── Typewriter that loops: types → waits → erases → waits → types again ── */
function Typewriter({ text, typingSpeed = 80, pauseAfterType = 8000, eraseSpeed = 40, pauseAfterErase = 500 }) {
  const [displayed, setDisplayed] = useState("");
  const [phase, setPhase] = useState("typing"); // typing | paused | erasing | waiting

  useEffect(() => {
    let timer;
    if (phase === "typing") {
      if (displayed.length < text.length) {
        timer = setTimeout(() => setDisplayed(text.slice(0, displayed.length + 1)), typingSpeed);
      } else {
        timer = setTimeout(() => setPhase("erasing"), pauseAfterType);
      }
    } else if (phase === "erasing") {
      if (displayed.length > 0) {
        timer = setTimeout(() => setDisplayed(displayed.slice(0, -1)), eraseSpeed);
      } else {
        timer = setTimeout(() => setPhase("typing"), pauseAfterErase);
      }
    }
    return () => clearTimeout(timer);
  }, [displayed, phase, text, typingSpeed, pauseAfterType, eraseSpeed, pauseAfterErase]);

  return (
    <span className="typewriter-wrap">
      {displayed}
      <span className="typewriter-cursor">|</span>
    </span>
  );
}

/* ── Floating Dashboard Card (left side) ── */
function DashboardCard() {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <motion.div
      className="hero-card hero-card-left"
      initial={{ opacity: 0, x: -60, y: 20, rotate: -6 }}
      animate={{ opacity: 1, x: 0, y: 0, rotate: -6 }}
      transition={{ duration: 0.9, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Header row */}
      <div className="prod-header">
        <div>
          <div className="prod-label">Hours reclaimed this week</div>
          <div className="prod-time">47 hr 12 m</div>
        </div>
        <div className="prod-change">
          <div className="prod-change-top prod-change-pulse">
            <span className="prod-pct" style={{ color: "#16A34A" }}>+34%</span>
            <span className="prod-change-icon" style={{ color: "#16A34A" }}>▲</span>
          </div>
          <span className="prod-vs">vs last week</span>
        </div>
      </div>

      {/* Day labels */}
      <div className="prod-days">
        {days.map((d) => (
          <span key={d} className="prod-day">{d}</span>
        ))}
      </div>

      {/* Gantt-like bars */}
      <div className="prod-bars">
        <div className="prod-bar-row">
          <div
            className="prod-bar prod-bar-purple prod-bar-animated"
            style={{ "--bar-offset": "14%", "--bar-width": "38%", "--bar-delay": "0s" }}
          />
        </div>
        <div className="prod-bar-row">
          <div
            className="prod-bar prod-bar-blue prod-bar-animated"
            style={{ "--bar-offset": "30%", "--bar-width": "45%", "--bar-delay": "0.35s" }}
          />
        </div>
        <div className="prod-bar-row">
          <div
            className="prod-bar prod-bar-green prod-bar-animated"
            style={{ "--bar-offset": "22%", "--bar-width": "40%", "--bar-delay": "0.7s" }}
          />
        </div>
      </div>

      {/* Legend */}
      <div className="prod-legend">
        <span className="prod-legend-item">
          <span className="prod-legend-dot prod-dot-purple" />Reports
        </span>
        <span className="prod-legend-item">
          <span className="prod-legend-dot prod-dot-blue" />Approvals
        </span>
        <span className="prod-legend-item">
          <span className="prod-legend-dot prod-dot-green" />Data entry
        </span>
      </div>
    </motion.div>
  );
}

/* ── Floating Active Users Card (right side) ── */
function StatsCard() {
  return (
    <motion.div
      className="hero-card hero-card-right"
      initial={{ opacity: 0, x: 60, y: 20, rotate: 5 }}
      animate={{ opacity: 1, x: 0, y: 0, rotate: 5 }}
      transition={{ duration: 0.9, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Header */}
      <div className="au-header">
        <div className="au-left">
          <div className="au-label">Cost saved this month</div>
          <div className="au-number">₹4.32L</div>
        </div>
        <div className="au-right">
          <div className="au-pct-row">
            <span className="au-pct">62%</span>
            <span className="au-pct-icon">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M5 2L8 6H2L5 2Z" fill="#fff" />
              </svg>
            </span>
          </div>
          <span className="au-vs">vs last month</span>
        </div>
      </div>

      {/* Chart with tooltip */}
      <div className="au-chart-wrap">
        <div className="au-tooltip au-tooltip-animated">
          <div className="au-tooltip-val">₹52K</div>
          <div className="au-tooltip-sub">peak day</div>
          <div className="au-tooltip-arrow" />
        </div>
        <svg className="au-chart-svg" viewBox="0 0 240 80" fill="none">
          <path
            className="au-chart-area"
            d="M0 55 L30 50 L60 42 L90 58 L120 38 L150 44 L180 30 L210 40 L240 35 L240 80 L0 80 Z"
            fill="url(#auGrad)"
          />
          <path
            className="au-chart-line"
            d="M0 55 L30 50 L60 42 L90 58 L120 38 L150 44 L180 30 L210 40 L240 35"
            stroke="#2BB5AE"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <line
            className="au-chart-guide"
            x1="150" y1="44" x2="150" y2="80"
            stroke="#2BB5AE" strokeWidth="1" strokeDasharray="3 3" opacity="0.4"
          />
          <circle className="au-chart-dot-pulse" cx="150" cy="44" r="5" fill="#2BB5AE" opacity="0.35" />
          <circle className="au-chart-dot" cx="150" cy="44" r="5" fill="#2BB5AE" stroke="#fff" strokeWidth="2.5" />
          <defs>
            <linearGradient id="auGrad" x1="0" y1="0" x2="0" y2="80">
              <stop offset="0%" stopColor="#2BB5AE" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#2BB5AE" stopOpacity="0.01" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </motion.div>
  );
}

/* ════════════════════════════════════════════
   HERO — Clean layout with floating cards
   ════════════════════════════════════════════ */
export default function HeroNew() {
  const [mounted, setMounted] = useState(false);
  const [wordIndex, setWordIndex] = useState(0);
  const { currency } = useCurrency();
  const isIndia = currency === "INR";

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % rotatingWords.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      id="hero"
      className="hero-section"
    >
      {/* Background gradient */}
      <div className="hero-bg" />

      {/* Floating cards */}
      {mounted && (
        <>
          <DashboardCard />
          <StatsCard />
        </>
      )}

      {/* ── Center content ── */}
      <div className="hero-content">
        {/* Badge */}
        <motion.div
          className="hero-badge"
          initial={{ opacity: 0, y: 16, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <span className="hero-badge-dot" />
          <span>AI Transformation Platform</span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          className="hero-h1"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="hero-h1-line1">Your company isn't slow.</span>
          <span className="hero-h1-line2">
            It's just not{" "}
              <span className="hero-highlight-wrapper">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={rotatingWords[wordIndex]}
                    className="hero-highlight"
                    initial={{ opacity: 0, filter: "blur(4px)" }}
                    animate={{ opacity: 1, filter: "blur(0px)" }}
                    exit={{ opacity: 0, filter: "blur(4px)" }}
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                  >
                    {rotatingWords[wordIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
              {" "}yet!
          </span>
        </motion.h1>

        {/* Subline — typewriter */}
        <motion.p
          className="hero-sub"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
        >
          <Typewriter text="We help you transform it." />
        </motion.p>

        {/* Description */}
        <motion.p
          className="hero-desc"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.65 }}
        >
          This offering is designed for firms and individuals with budgets greater than {isIndia ? "₹5 lakh" : "$5,000"}.<br />
          {/* qualify for this. */}
        </motion.p>

        {/* CTAs */}
        <motion.div
          className="hero-ctas"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.8 }}
        >
          <BookAuditButton variant="primary" className="hero-cta-main" />
          <a
            href="#calculator"
            className="btn-ghost hero-cta-calc"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById("calculator");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Calculate your waste
          </a>
        </motion.div>

        {/* <motion.span
          className="hero-note"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0 }}
        >
          Free 30-min audit &middot; No commitment &middot; Just numbers
        </motion.span> */}

        {/* Trust row */}
        <motion.div
          className="hero-trust"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.1 }}
        >
          <div className="hero-trust-item">
            <span className="hero-trust-val"><CountUp target={14} /></span>
            <span className="hero-trust-lbl">Organisations</span>
          </div>
          <div className="hero-trust-sep" />
          <div className="hero-trust-item">
            <span className="hero-trust-val"><CountUp target={12} prefix="₹" suffix="Cr+" /></span>
            <span className="hero-trust-lbl">Revenue Saved</span>
          </div>
          <div className="hero-trust-sep" />
          <div className="hero-trust-item">
            <span className="hero-trust-val"><CountUp target={100} suffix="+" /></span>
            <span className="hero-trust-lbl">Workflows Built</span>
          </div>
        </motion.div>
      </div>

      <style>{`
        /* ── Hero Section ── */
        .hero-section {
          position: relative;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          padding: 100px var(--container-padding) 50px;
        }

        /* ── Background gradient — soft mint/teal ── */
        .hero-bg {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            180deg,
            #ffffff 0%,
            #edfcfb 25%,
            #d4f5f3 50%,
            #c0efec 70%,
            #edfcfb 90%,
            #ffffff 100%
          );
          z-index: 0;
        }

        /* Dark mode background */
        .resource-optimizer-page:not(.light) .hero-bg {
          background: linear-gradient(
            180deg,
            var(--bg-base) 0%,
            rgba(40,180,174,0.06) 30%,
            rgba(40,180,174,0.10) 50%,
            rgba(40,180,174,0.06) 70%,
            var(--bg-base) 100%
          );
        }

        /* ── Floating Cards ── */
        .hero-card {
          position: absolute;
          z-index: 1;
          background: #ffffff;
          border-radius: 20px;
          padding: 22px;
          box-shadow: 0 8px 40px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04);
          border: 1px solid rgba(0,0,0,0.06);
          animation: card-float 6s ease-in-out infinite;
        }

        .hero-card-left {
          left: 6%;
          top: 22%;
          width: 280px;
          animation-delay: 0s;
        }

        .hero-card-right {
          right: 8%;
          top: 25%;
          width: 260px;
          animation-delay: 1.5s;
        }

        @keyframes card-float {
          0%, 100% { transform: translateY(0) rotate(var(--card-rotate, 0deg)); }
          50% { transform: translateY(-12px) rotate(var(--card-rotate, 0deg)); }
        }

        .hero-card-left { --card-rotate: -6deg; }
        .hero-card-right { --card-rotate: 5deg; }

        /* ── Productivity Card (left) ── */
        .prod-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .prod-label {
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 4px;
        }

        .prod-time {
          font-family: 'Inter', sans-serif;
          font-size: 28px;
          font-weight: 800;
          color: #1f2937;
          letter-spacing: -0.03em;
          line-height: 1;
        }

        .prod-change {
          text-align: right;
        }

        .prod-change-top {
          display: flex;
          align-items: center;
          gap: 4px;
          justify-content: flex-end;
        }

        .prod-pct {
          font-family: 'Inter', sans-serif;
          font-size: 16px;
          font-weight: 700;
          color: #1f2937;
        }

        .prod-change-icon {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #e5e7eb;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 700;
          color: #6b7280;
          line-height: 1;
        }

        .prod-vs {
          font-family: 'Inter', sans-serif;
          font-size: 10px;
          color: #9ca3af;
          font-weight: 400;
        }

        .prod-days {
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;
        }

        .prod-day {
          font-family: 'Inter', sans-serif;
          font-size: 10px;
          font-weight: 500;
          color: #9ca3af;
          flex: 1;
          text-align: center;
        }

        .prod-bars {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 14px;
        }

        .prod-bar-row {
          height: 12px;
          position: relative;
        }

        .prod-bar {
          height: 100%;
          border-radius: 6px;
          position: absolute;
          top: 0;
        }

        .prod-bar-animated {
          left: var(--bar-offset);
          width: 0;
          transform-origin: left center;
          animation: bar-grow 5s cubic-bezier(0.22, 1, 0.36, 1) infinite;
          animation-delay: var(--bar-delay);
        }

        @keyframes bar-grow {
          0%   { width: 0; opacity: 0.5; }
          18%  { width: var(--bar-width); opacity: 1; }
          82%  { width: var(--bar-width); opacity: 1; }
          100% { width: 0; opacity: 0.5; }
        }

        .prod-change-pulse {
          animation: pct-pulse 3.4s ease-in-out infinite;
          transform-origin: right center;
        }

        @keyframes pct-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.06); }
        }

        .prod-bar-purple { background: linear-gradient(90deg, #8B5CF6, #A78BFA); }
        .prod-bar-blue { background: linear-gradient(90deg, #3B82F6, #60A5FA); }
        .prod-bar-green { background: linear-gradient(90deg, #22C55E, #4ADE80); }

        .prod-legend {
          display: flex;
          gap: 14px;
          flex-wrap: wrap;
        }

        .prod-legend-item {
          display: flex;
          align-items: center;
          gap: 5px;
          font-family: 'Inter', sans-serif;
          font-size: 10px;
          font-weight: 500;
          color: #374151;
        }

        .prod-legend-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .prod-dot-purple { background: #8B5CF6; }
        .prod-dot-blue { background: #3B82F6; }
        .prod-dot-green { background: #22C55E; }

        /* ── Right card (Active Users) ── */
        .au-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }

        .au-label {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 500;
          color: #6b7280;
          margin-bottom: 4px;
        }

        .au-number {
          font-family: 'Inter', sans-serif;
          font-size: 34px;
          font-weight: 800;
          color: #1f2937;
          letter-spacing: -0.03em;
          line-height: 1;
        }

        .au-right {
          text-align: right;
          padding-top: 2px;
        }

        .au-pct-row {
          display: flex;
          align-items: center;
          gap: 5px;
          justify-content: flex-end;
        }

        .au-pct {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 700;
          color: #1f2937;
        }

        .au-pct-icon {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #22c55e;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .au-vs {
          font-family: 'Inter', sans-serif;
          font-size: 10px;
          color: #9ca3af;
          font-weight: 400;
        }

        /* Chart area */
        .au-chart-wrap {
          position: relative;
          margin-top: 8px;
        }

        .au-chart-svg {
          width: 100%;
          height: 70px;
          display: block;
          overflow: visible;
        }

        .au-chart-line {
          stroke-dasharray: 520;
          stroke-dashoffset: 520;
          animation: line-draw 5s cubic-bezier(0.65, 0, 0.35, 1) infinite;
        }

        @keyframes line-draw {
          0%   { stroke-dashoffset: 520; }
          30%  { stroke-dashoffset: 0; }
          85%  { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: 520; }
        }

        .au-chart-area {
          opacity: 0;
          animation: area-fade 5s ease-in-out infinite;
        }

        @keyframes area-fade {
          0%, 20%  { opacity: 0; }
          45%      { opacity: 1; }
          80%      { opacity: 1; }
          100%     { opacity: 0; }
        }

        .au-chart-guide {
          opacity: 0;
          animation: guide-fade 5s ease-in-out infinite;
        }

        @keyframes guide-fade {
          0%, 45%  { opacity: 0; }
          55%      { opacity: 0.4; }
          82%      { opacity: 0.4; }
          100%     { opacity: 0; }
        }

        .au-chart-dot {
          opacity: 0;
          transform-origin: 150px 44px;
          transform-box: fill-box;
          animation: dot-appear 5s ease-in-out infinite;
        }

        @keyframes dot-appear {
          0%, 45%  { opacity: 0; transform: scale(0.4); }
          55%      { opacity: 1; transform: scale(1); }
          82%      { opacity: 1; transform: scale(1); }
          100%     { opacity: 0; transform: scale(0.4); }
        }

        .au-chart-dot-pulse {
          opacity: 0;
          transform-origin: 150px 44px;
          transform-box: fill-box;
          animation: dot-ripple 2.2s ease-out infinite;
          animation-delay: 2.7s;
        }

        @keyframes dot-ripple {
          0%   { opacity: 0.5; transform: scale(1); }
          100% { opacity: 0;   transform: scale(2.6); }
        }

        /* Tooltip */
        .au-tooltip {
          position: absolute;
          top: -8px;
          left: 54%;
          transform: translateX(-50%);
          background: #1f2937;
          border-radius: 8px;
          padding: 6px 10px;
          text-align: center;
          z-index: 2;
          white-space: nowrap;
        }

        .au-tooltip-val {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 700;
          color: #fff;
          line-height: 1.2;
        }

        .au-tooltip-sub {
          font-family: 'Inter', sans-serif;
          font-size: 10px;
          font-weight: 400;
          color: #22c55e;
          line-height: 1.2;
        }

        .au-tooltip-arrow {
          position: absolute;
          bottom: -5px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-top: 5px solid #1f2937;
        }

        .au-tooltip-animated {
          opacity: 0;
          animation: tooltip-reveal 5s ease-in-out infinite;
        }

        @keyframes tooltip-reveal {
          0%, 45%  { opacity: 0; transform: translateX(-50%) translateY(8px); }
          58%      { opacity: 1; transform: translateX(-50%) translateY(-2px); }
          64%      { opacity: 1; transform: translateX(-50%) translateY(0); }
          82%      { opacity: 1; transform: translateX(-50%) translateY(0); }
          100%     { opacity: 0; transform: translateX(-50%) translateY(8px); }
        }

        /* Respect users who prefer no motion — hold final state */
        @media (prefers-reduced-motion: reduce) {
          .prod-bar-animated,
          .prod-change-pulse,
          .au-chart-line,
          .au-chart-area,
          .au-chart-guide,
          .au-chart-dot,
          .au-chart-dot-pulse,
          .au-tooltip-animated,
          .hero-card {
            animation: none !important;
          }
          .prod-bar-animated { width: var(--bar-width); }
          .au-chart-line    { stroke-dashoffset: 0; }
          .au-chart-area,
          .au-chart-dot,
          .au-tooltip-animated { opacity: 1; }
          .au-chart-guide   { opacity: 0.4; }
          .au-tooltip-animated { transform: translateX(-50%); }
        }

        /* Dark mode cards */
        .resource-optimizer-page:not(.light) .hero-card {
          background: var(--bg-elevated);
          border-color: var(--border-default);
          box-shadow: 0 8px 40px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2);
        }

        .resource-optimizer-page:not(.light) .prod-label,
        .resource-optimizer-page:not(.light) .au-label { color: var(--text-secondary); }
        .resource-optimizer-page:not(.light) .prod-time,
        .resource-optimizer-page:not(.light) .prod-pct,
        .resource-optimizer-page:not(.light) .au-number,
        .resource-optimizer-page:not(.light) .au-pct { color: var(--text-primary); }
        .resource-optimizer-page:not(.light) .prod-vs,
        .resource-optimizer-page:not(.light) .prod-day,
        .resource-optimizer-page:not(.light) .au-vs { color: var(--text-muted); }
        .resource-optimizer-page:not(.light) .prod-legend-item { color: var(--text-secondary); }
        .resource-optimizer-page:not(.light) .prod-change-icon { background: rgba(255,255,255,0.1); color: var(--text-muted); }
        .resource-optimizer-page:not(.light) .au-tooltip { background: #fff; }
        .resource-optimizer-page:not(.light) .au-tooltip-val { color: #1f2937; }
        .resource-optimizer-page:not(.light) .au-tooltip-arrow { border-top-color: #fff; }

        /* ── Content ── */
        .hero-content {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          max-width: 700px;
        }

        /* ── Badge ── */
        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 12px 28px 12px 20px;
          border-radius: 100px;
          background: rgba(255,255,255,0.85);
          border: 1px solid rgba(40,180,174,0.15);
          font-family: 'Inter', sans-serif;
          font-size: 16px;
          font-weight: 700;
          color: #28B4AE;
          margin-bottom: 28px;
          cursor: default;
          backdrop-filter: blur(12px);
          box-shadow: 0 2px 16px rgba(40,180,174,0.08);
        }

        .hero-badge-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #28B4AE;
          flex-shrink: 0;
        }

        .resource-optimizer-page:not(.light) .hero-badge {
          background: rgba(255,255,255,0.05);
          border-color: rgba(40,180,174,0.18);
          color: var(--accent);
          box-shadow: 0 2px 16px rgba(0,0,0,0.2);
        }

        /* ── Headline ── */
        .hero-h1 {
          font-family: 'Inter', sans-serif;
          font-weight: 800;
          font-size: clamp(36px, 5.5vw, 64px);
          line-height: 1.1;
          letter-spacing: -0.04em;
          color: #1a1a1a;
          margin: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .resource-optimizer-page:not(.light) .hero-h1 {
          color: var(--text-primary);
        }

        .hero-h1-line1 {
          display: block;
          margin-bottom: 4px;
        }

        .hero-h1-line2 {
          display: block;
          font-weight: 500;
          font-size: 0.58em;
          color: #555;
          line-height: 1.4;
        }

        .resource-optimizer-page:not(.light) .hero-h1-line2 {
          color: var(--text-secondary);
        }

        .hero-highlight-wrapper {
          display: inline-block;
          vertical-align: baseline;
        }

        .hero-highlight {
          display: inline-block;
          font-weight: 800;
          color: #28B4AE;
          font-style: italic;
        }

        /* ── Subline ── */
        .hero-sub {
          font-family: 'Inter', sans-serif;
          font-weight: 700;
          font-size: clamp(18px, 2.2vw, 26px);
          color: #28B4AE;
          margin: 16px 0 0;
          letter-spacing: -0.02em;
        }

        .resource-optimizer-page:not(.light) .hero-sub {
          background: linear-gradient(135deg, var(--accent) 0%, #5de0db 70%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* ── Typewriter ── */
        .typewriter-wrap {
          display: inline;
        }

        .typewriter-cursor {
          display: inline-block;
          font-weight: 300;
          margin-left: 1px;
          animation: blink-cursor 0.7s step-end infinite;
        }

        @keyframes blink-cursor {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }

        /* ── Description ── */
        .hero-desc {
          font-family: 'Inter', sans-serif;
          font-weight: 400;
          font-size: 15px;
          line-height: 1.7;
          color: #777;
          margin: 12px 0 0;
          max-width: 420px;
        }

        .resource-optimizer-page:not(.light) .hero-desc {
          color: var(--text-muted);
        }

        /* ── CTAs ── */
        .hero-ctas {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-top: 28px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .hero-cta-main {
          padding: 14px 32px !important;
          font-size: 14px !important;
          border-radius: 10px !important;
        }

        .hero-cta-calc {
          padding: 14px 24px !important;
          font-size: 14px !important;
          border-radius: 10px !important;
          background: #fff !important;
          border-color: rgba(0,0,0,0.08) !important;
        }

        .hero-note {
          display: block;
          margin-top: 12px;
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          color: #999;
          opacity: 0.7;
        }

        .resource-optimizer-page:not(.light) .hero-note {
          color: var(--text-muted);
        }

        /* ── Trust ── */
        .hero-trust {
          display: flex;
          align-items: center;
          margin-top: 48px;
          padding: 20px 0;
          border-top: 1px solid rgba(0,0,0,0.08);
        }

        .resource-optimizer-page:not(.light) .hero-trust {
          border-top-color: var(--border-default);
        }

        .hero-trust-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding: 0 32px;
        }

        .hero-trust-sep {
          width: 1px;
          height: 36px;
          background: rgba(0,0,0,0.08);
          flex-shrink: 0;
        }

        .resource-optimizer-page:not(.light) .hero-trust-sep {
          background: linear-gradient(180deg, transparent, var(--border-default), transparent);
        }

        .hero-trust-val {
          font-family: 'Inter', sans-serif;
          font-weight: 800;
          font-size: clamp(26px, 2.5vw, 34px);
          color: #1a1a1a;
          line-height: 1;
          letter-spacing: -0.03em;
        }

        .resource-optimizer-page:not(.light) .hero-trust-val {
          color: var(--text-primary);
        }

        .hero-trust-lbl {
          font-family: 'Inter', sans-serif;
          font-size: 10px;
          font-weight: 500;
          color: #999;
          text-transform: uppercase;
          letter-spacing: 0.12em;
        }

        .resource-optimizer-page:not(.light) .hero-trust-lbl {
          color: var(--text-muted);
        }

        /* ── Responsive ── */
        @media (max-width: 1200px) {
          .hero-card-left { left: 3%; }
          .hero-card-right { right: 3%; }
        }

        @media (max-width: 1024px) {
          .hero-card { padding: 16px; }
          .hero-card-left { left: 2%; top: 22%; width: 240px; }
          .hero-card-right { right: 2%; top: 20%; width: 200px; }
          .au-number { font-size: 26px; }
          .prod-time { font-size: 22px; }
        }

        @media (max-width: 768px) {
          .hero-card { display: none; }
        }

        @media (max-width: 640px) {
          .hero-ctas {
            flex-direction: column;
            width: 100%;
          }
          .hero-cta-main, .hero-cta-calc {
            width: 100%;
            text-align: center;
            justify-content: center;
          }
          .hero-trust-item { padding: 0 18px; }
          .hero-h1-line2 { font-size: 0.52em; }
        }

        @media (max-width: 480px) {
          .hero-trust { flex-wrap: wrap; gap: 14px; }
          .hero-trust-sep { display: none; }
          .hero-trust-item { padding: 0; flex: 1; min-width: 80px; }
        }
      `}</style>
    </section>
  );
}
